import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import type {
  DirectionFilter,
  NetworkPointsParams,
  TradingMarketFilter,
} from 'src/apis/network-points/types';
import { useNetworkPoints } from 'src/apis/network-points/use-network-points';
import { useNetworkPointsRoot } from 'src/apis/network-points/use-network-points-root';
import { type NetworkPointPopoverParams } from 'src/components/form/select/network-point-select/network-point-popover';
import { useReauthenticate } from 'src/hooks/use-reauthenticate';
import { useSearch } from 'src/hooks/use-search';
import { handleErrorWithNotification } from 'src/utils/handle-error';

export function useNetworkPointSelectSearch({
  initialSearch,
  tradingMarket,
  direction,
}: {
  initialSearch?: string;
  tradingMarket?: TradingMarketFilter;
  direction?: DirectionFilter | DirectionFilter[];
}) {
  const networkPointsRoot = useNetworkPointsRoot({
    // special error handling case: we'd like to immidiately ask for the network points root,
    // but we'd like to postpone an error notification until someone actually interacts with the search
    onError(error) {
      return error;
    },
  });
  const [_, setReauthenticate] = useReauthenticate();
  const firstUserInteraction = useRef(true);

  const [internalParams, setInternalParams] =
    useState<NetworkPointPopoverParams>({
      q: initialSearch ?? null,
      favouritesAboveIfInitialSearch: false,
      case: 'ACTIVE_ONLY',
      limit: 20,
      offset: 0,
    });

  const params = useMemo(() => {
    const params: NetworkPointsParams = {
      q: internalParams.q,
      offset: internalParams.offset,
      limit: internalParams.limit,
      favouritesAboveIfInitialSearch:
        internalParams.favouritesAboveIfInitialSearch,
      favorite:
        internalParams.case === 'ONLY_FAVORITE' ? 'ONLY_FAVORITE' : 'ALL',
      own: internalParams.case === 'ONLY_OWN' ? 'ONLY_OWN' : 'ALL',
      active: internalParams.case === 'ACTIVE_ONLY' ? 'ACTIVE' : 'ALL',
      tradingMarket,
      direction,
    };
    return params;
  }, [internalParams, tradingMarket, direction]);

  const minLength = 0;

  const [searchValue, setSearchValue, searchQuery] = useSearch(initialSearch, {
    minLength,
    delay: 100,
  });

  const networkPoints = useNetworkPoints();

  useEffect(() => {
    if (!networkPointsRoot.response) return;
    networkPoints.execute({ networkPointsRoot, params });
    // oxlint-disable-next-line exhaustive-deps
  }, [networkPointsRoot.response, params]);

  useEffect(() => {
    // as we handle a request _within_ the form and not outside of it,
    // we need to handle constraint violation errors differently then usualy
    if (!networkPoints.error) return;
    // only show one error at a time
    const errorMessage =
      networkPoints.error.response.data.violations[0]!.message;
    toast.error(`The search term is not valid. (${errorMessage})`);
  }, [networkPoints.error]);

  useEffect(() => {
    setInternalParams((params) => ({
      ...params,
      q: searchQuery,
    }));
  }, [searchQuery]);

  const loadMoreNetworkPoints = useNetworkPoints();

  // whenever params change we reset the "load more" request
  useEffect(() => {
    loadMoreNetworkPoints.canceler?.();
    loadMoreNetworkPoints.setResponse(null);
    // oxlint-disable-next-line exhaustive-deps
  }, [internalParams]);

  // special error handling case: we'd like to immidiately ask for the network points root,
  // but we'd like to postpone an error notification until someone actually interacts with the search
  const onFocus = () => {
    if (!firstUserInteraction.current) return;
    firstUserInteraction.current = false;
    if (!networkPointsRoot.error) return;
    handleErrorWithNotification({
      error: networkPointsRoot.error,
      setReauthenticate,
    });
  };

  const searchValueForCurrentOptions = (
    networkPoints.response?.config.params as NetworkPointsParams | undefined
  )?.q;

  return {
    networkPointsRoot,
    networkPoints,
    loadMoreNetworkPoints,
    params,
    internalParams,
    setInternalParams,
    searchValue,
    setSearchValue,
    onFocus,
    searchValueForCurrentOptions,
  };
}
